import { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTaskStore } from '../store/useTaskStore';
import { CreateTaskData, TaskStatus, UpdateTaskData } from '../types/task.types';

export const useTasks = () => {
  // Use the global task store
  const {
    tasks,
    loading,
    error,
    fetchTasks,
    createTask: storeCreateTask,
    updateTask: storeUpdateTask,
    deleteTask: storeDeleteTask,
    toggleTaskStatus: storeToggleTaskStatus,
    reorderTask: storeReorderTask,
    updatePositions: storeUpdatePositions,
    addSubtask: storeAddSubtask,
    toggleSubtask: storeToggleSubtask,
    updateSubtask: storeUpdateSubtask,
    deleteSubtask: storeDeleteSubtask,
    reorderSubtask: storeReorderSubtask,
    setTasks
  } = useTaskStore();

  // Create task - using store method
  const createTask = useCallback(async (data: CreateTaskData) => {
    try {
      return await storeCreateTask(data);
    } catch (err) {
      console.error('Error creating task:', err);
      throw err;
    }
  }, [storeCreateTask]);

  // Update task - using store method
  const updateTask = useCallback(async (taskId: string, data: UpdateTaskData) => {
    try {
      return await storeUpdateTask(taskId, data);
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  }, [storeUpdateTask]);

  // Toggle task status - using store method
  const toggleTaskStatus = useCallback(async (taskId: string, currentStatus: TaskStatus) => {
    try {
      await storeToggleTaskStatus(taskId, currentStatus);
    } catch (error) {
      console.error('Error toggling task status:', error);
      toast.error('Failed to update task status. Please try again.');
    }
  }, [storeToggleTaskStatus]);

  // Reorder task - using store method
  const reorderTask = useCallback(async (taskId: string, newPosition: number) => {
    try {
      await storeReorderTask(taskId, newPosition);
    } catch (error) {
      console.error('Error reordering task:', error);
    }
  }, [storeReorderTask]);

  // Update positions - using store method
  const updatePositions = useCallback(async (status: TaskStatus, taskIds: string[]) => {
    try {
      await storeUpdatePositions(status, taskIds);
    } catch (error) {
      console.error("Error updating task positions:", error);
    }
  }, [storeUpdatePositions]);

  // Update subtask - using store method
  const updateSubtask = useCallback(async (taskId: string, subtaskId: string, title: string) => {
    try {
      await storeUpdateSubtask(taskId, subtaskId, title);
    } catch (error) {
      console.error('Error updating subtask:', error);
      toast.error('Failed to update subtask');
    }
  }, [storeUpdateSubtask]);

  // Delete subtask - using store method
  const deleteSubtask = useCallback(async (taskId: string, subtaskId: string) => {
    try {
      await storeDeleteSubtask(taskId, subtaskId);
    } catch (error) {
      console.error('Error deleting subtask:', error);
      toast.error('Failed to delete subtask');
    }
  }, [storeDeleteSubtask]);

  // Reorder subtasks - using store method
  const reorderSubtask = useCallback(async (
    taskId: string,
    action: ['move-up' | 'move-down', string]
  ) => {
    try {
      await storeReorderSubtask(taskId, action);
    } catch (error) {
      console.error('Error reordering subtask:', error);
      toast.error('Failed to reorder subtask');
    }
  }, [storeReorderSubtask]);

  // No longer fetch tasks on mount - this is now handled by TasksInitializer

  // Simplified delete task method
  const deleteTask = useCallback(async (taskId: string) => {
    try {
      await storeDeleteTask(taskId);
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  }, [storeDeleteTask]);

  // Simplified add subtask method
  const addSubtask = useCallback(async (taskId: string, title: string) => {
    try {
      await storeAddSubtask(taskId, title);
    } catch (error) {
      console.error('Error adding subtask:', error);
    }
  }, [storeAddSubtask]);

  // Simplified toggle subtask method
  const toggleSubtask = useCallback(async (taskId: string, subtaskId: string) => {
    try {
      await storeToggleSubtask(taskId, subtaskId);
    } catch (error) {
      console.error('Error toggling subtask:', error);
    }
  }, [storeToggleSubtask]);

  return {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskStatus,
    reorderTask,
    addSubtask,
    toggleSubtask,
    updateSubtask,
    deleteSubtask,
    reorderSubtask,
    setTasks,
    updatePositions,
    // Add a method to refresh tasks explicitly
    refreshTasks: fetchTasks
  };
};
